/**
 * Compresses an image file by resizing it to a maximum dimension and reducing quality.
 * Includes a timeout and fallback to original file if compression fails.
 * @param file The original File object
 * @param maxWidth The maximum width/height (default 1200px)
 * @param quality The JPEG quality (0 to 1, default 0.8)
 * @returns A Promise resolving to the compressed base64 string (without prefix)
 */
export async function compressImage(file: File, maxWidth = 1200, quality = 0.8): Promise<string> {
    const getOriginalBase64 = (): Promise<string> => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const res = reader.result as string;
            resolve(res.includes(',') ? res.split(',')[1] : res);
        };
        reader.onerror = (err) => reject(err);
    });

    const compressPromise = new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;
            img.onload = () => {
                try {
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                        if (width > maxWidth) {
                            height *= maxWidth / width;
                            width = maxWidth;
                        }
                    } else {
                        if (height > maxWidth) {
                            width *= maxWidth / height;
                            height = maxWidth;
                        }
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error("Could not get canvas context"));
                        return;
                    }
                    ctx.drawImage(img, 0, 0, width, height);

                    const dataUrl = canvas.toDataURL('image/jpeg', quality);
                    resolve(dataUrl.split(',')[1]);
                } catch (err) {
                    reject(err);
                }
            };
            img.onerror = (err) => reject(new Error("Image Load Error: " + String(err)));
        };
        reader.onerror = (err) => reject(new Error("FileReader Error: " + String(err)));
    });

    const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("Compression timeout")), 10000);
    });

    try {
        return await Promise.race([compressPromise, timeoutPromise]);
    } catch (error) {
        console.warn("[compressImage] Failed or timed out. Falling back to original file base64. Reason:", error);
        return await getOriginalBase64();
    }
}
